import React, { createElement } from 'react';
import { Card, CardContent } from '../ui/card';
import { Clock, CheckCircle, AlertCircle, ImageIcon, Quote, FileText, Film, Play } from 'lucide-react';
import { TranscriptionResponse } from '../../lib/types';
import { Link, useNavigate } from 'react-router-dom';
import { cn } from '../../lib/utils';
interface TranscriptionCardProps {
  transcription: TranscriptionResponse;
}
export function TranscriptionCard({
  transcription
}: TranscriptionCardProps) {
  const navigate = useNavigate();
  // Helper function to parse TLDR from string to array
  const parseTLDR = (tldrData?: string | string[]): string[] => {
    if (!tldrData) return [];
    // If tldrData is already an array, return it
    if (Array.isArray(tldrData)) {
      return tldrData;
    }
    // Otherwise try to parse it as a JSON string
    try {
      return JSON.parse(tldrData);
    } catch {
      return [];
    }
  };
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return <span className="flex items-center gap-1 text-xs font-medium text-success bg-success/10 px-2 py-0.5 rounded-full" data-id="element-1418">
            <CheckCircle size={12} data-id="element-1419" />
            <span data-id="element-1420">Completed</span>
          </span>;
      case 'processing':
      case 'pending':
        return <span className="flex items-center gap-1 text-xs font-medium text-primary bg-primary/10 px-2 py-0.5 rounded-full" data-id="element-1421">
            <Clock size={12} className="animate-spin" data-id="element-1422" />
            <span data-id="element-1423">Processing</span>
          </span>;
      case 'failed':
        return <span className="flex items-center gap-1 text-xs font-medium text-destructive bg-destructive/10 px-2 py-0.5 rounded-full" data-id="element-1424">
            <AlertCircle size={12} data-id="element-1425" />
            <span data-id="element-1426">Failed</span>
          </span>;
      default:
        return null;
    }
  };
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    if (date.toDateString() === now.toDateString()) {
      return `Today, ${date.toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit'
      })}`;
    }
    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);
    if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    }
    return date.toLocaleDateString();
  };
  const truncateId = (id?: string | null) => {
    if (!id) return 'Unknown ID';
    return id.length > 16 ? `${id.substring(0, 8)}...` : id;
  };
  const getThumbnailUrl = () => {
    if (transcription.status === 'completed') {
      return transcription.supabase_thumbnail_url || transcription.square_thumbnail_url || transcription.thumbnail_url || transcription.thumbnail;
    }
    return null;
  };
  // Custom placeholder for when no thumbnail is available
  const renderPlaceholder = () => {
    const title = transcription.title || 'Untitled Video';
    const platform = transcription.platform || 'TikTok';
    const initials = title.substring(0, 2).toUpperCase();
    return <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-gray-700 to-gray-900 relative overflow-hidden" data-id="element-1427">
        <div className="absolute inset-0 opacity-10" data-id="element-1428">
          <div className="absolute top-1/4 left-1/4 w-1/2 h-1/2 rounded-full bg-primary/20" data-id="element-1429"></div>
          <div className="absolute bottom-1/4 right-1/4 w-1/3 h-1/3 rounded-full bg-primary/30" data-id="element-1430"></div>
        </div>
        <Film size={32} className="text-gray-400 mb-3" data-id="element-1431" />
        <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center mb-3" data-id="element-1432">
          <span className="text-lg font-bold text-white" data-id="element-1433">{initials}</span>
        </div>
        <div className="text-center px-4" data-id="element-1434">
          <p className="text-white/80 text-sm font-medium mb-1 line-clamp-1" data-id="element-1435">
            {title}
          </p>
          <p className="text-white/50 text-xs" data-id="element-1436">{platform} Transcript</p>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-1/6 bg-gradient-to-t from-black/40 to-transparent" data-id="element-1437"></div>
      </div>;
  };
  const thumbnailUrl = getThumbnailUrl();
  const hasInsights = transcription.quote || transcription.tldr && parseTLDR(transcription.tldr).length > 0;
  const tldrItems = transcription.tldr ? parseTLDR(transcription.tldr) : [];
  const handleCardClick = () => {
    // Make sure we're using the correct path format
    navigate(`/app/transcriptions/${transcription.task_id}`);
  };
  return <Card className={cn('h-full transition-all duration-200 cursor-pointer', 'hover:border-primary/40 hover:shadow-md hover:shadow-primary/5', 'focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2', 'active:scale-[0.98] touch-action-manipulation')} onClick={handleCardClick} data-id="element-1438">
      <div className="relative" data-id="element-1439">
        <div className="w-full h-32 sm:h-40 bg-secondary/30 rounded-t-lg flex items-center justify-center overflow-hidden" data-id="element-1440">
          {thumbnailUrl ? <img src={thumbnailUrl} alt={transcription.title || 'Video thumbnail'} className="w-full h-full object-cover" onError={e => {
          e.currentTarget.style.display = 'none';
          const container = e.currentTarget.parentElement;
          if (container) {
            // Replace with our custom placeholder instead of showing error text
            container.appendChild((() => {
              const div = document.createElement('div');
              div.className = 'w-full h-full';
              // Use ReactDOM to render our placeholder
              // Since we can't use JSX directly in this callback
              const placeholder = document.createElement('div');
              placeholder.className = 'w-full h-full';
              container.innerHTML = '';
              container.appendChild(placeholder);
              // Create a styled placeholder with gradient background
              placeholder.innerHTML = `
                        <div class="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-gray-700 to-gray-900 relative">
                          <div class="text-gray-400 mb-2">
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2v8"></path><path d="m4.93 10.93 1.41 1.41"></path><path d="M2 18h2"></path><path d="M20 18h2"></path><path d="m19.07 10.93-1.41 1.41"></path><path d="M22 22H2"></path><path d="m16 6-4 4-4-4"></path><path d="M16 18a4 4 0 0 0-8 0"></path></svg>
                          </div>
                          <span class="text-gray-300 font-medium text-sm mb-1">${transcription.title ? transcription.title.substring(0, 20) + (transcription.title.length > 20 ? '...' : '') : 'Video Thumbnail'}</span>
                          <span class="text-gray-400 text-xs">${transcription.platform || 'TikTok'} Transcript</span>
                        </div>
                      `;
              return div;
            })());
          }
        }} data-id="element-1441" /> : renderPlaceholder()}
        </div>
        {transcription.status !== 'completed' && <div className="absolute bottom-2 right-2 py-0.5 px-1.5 bg-primary/80 backdrop-blur-sm text-white text-xs rounded" data-id="element-1442">
            {transcription.status === 'processing' ? 'Processing...' : transcription.status === 'pending' ? 'Pending...' : transcription.status}
          </div>}
        {/* New badge for TLDR/Quote */}
        {hasInsights && transcription.status === 'completed' && <div className="absolute top-2 left-2 py-0.5 px-2 bg-primary/80 backdrop-blur-sm text-white text-xs rounded-full flex items-center" data-id="element-1443">
            <FileText size={10} className="mr-1" data-id="element-1444" />
            <span data-id="element-1445">TLDR Ready</span>
          </div>}
      </div>
      <CardContent className="p-3 md:p-4" data-id="element-1446">
        <div className="flex justify-between items-center mb-2" data-id="element-1447">
          <div className="flex items-center text-xs text-muted-foreground" data-id="element-1448">
            <Clock size={12} className="mr-1" data-id="element-1449" />
            <span data-id="element-1450">{formatDate(transcription.created_at)}</span>
          </div>
          <div data-id="element-1451">{getStatusBadge(transcription.status)}</div>
        </div>
        <h3 className="font-medium text-sm md:text-base line-clamp-2 mb-1" data-id="element-1452">
          {transcription.title || `Untitled (${truncateId(transcription.video_id)})`}
        </h3>
        {/* Show Quote instead of ID if available */}
        {transcription.quote ? <p className="text-xs text-primary/80 italic line-clamp-2 mt-1" data-id="element-1453">
            "{transcription.quote}"
          </p> : <p className="text-xs text-muted-foreground truncate" data-id="element-1454">
            ID: {transcription.video_id || transcription.task_id}
          </p>}
        {/* TLDR Indicator */}
        {tldrItems.length > 0 && <div className="mt-2 flex items-center" data-id="element-1455">
            <span className="text-xs text-primary/80 flex items-center" data-id="element-1456">
              <FileText size={10} className="mr-1" data-id="element-1457" />
              {tldrItems.length} key points available
            </span>
          </div>}
      </CardContent>
    </Card>;
}